import React, { useEffect, useMemo, useState } from 'react';
import { Adjust } from '@cloudinary/url-gen/actions/adjust';
import { useNavigate, useLocation } from 'react-router';
import { TwoColumnAppType, TwoColumnPropertiesType } from '@appTypes/CategoryAppType';
import { SiteStructureType } from '@appTypes/siteStructureType';
import FeedbackSurvey from '@components/feedbacksurvey/FeedbackSurvey';
import { Layout } from '@components/layout/Layout';
import { useCloudinary } from '@hooks/Cloudinary/useCloudinary';
import { useWindowLocation } from '@hooks/Window/useWindowLocation';
import ErrorBoundary from '@components/error-component/ErrorBoundary';
import { InternalSearchCardType } from '@components/layout/InternalSearchMetaData';
import { FORM_SUBMITTED_EVENT } from '@components/block-content/types/components/Form/FormBlock.utils';
import { TabContainer } from './TabContainer/TabContainer';
import * as S from './TwoColumnApp.styles';
import { HeroContent } from './HeroContent/HeroContent';

export const TwoColumnApp = ({ data, appData }: { data: SiteStructureType; appData: TwoColumnAppType }) => {
  const { heroImage, heroContent, darkenBackground, linkPreview, tabs } = appData.properties;

  // Handle query string for linking into specific tabs
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isValgPage = location.pathname === '/valg';
  const updateSearchParams = (key: string, value: string | undefined) => {
    if (value) {
      searchParams.set(key, value);
      navigate({ search: searchParams.toString() });
    }
  };

  // Swap chat bubble text once a dynamic form in the tabs has been sent
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  useEffect(() => {
    const onFormSubmitted = () => setIsFormSubmitted(true);
    window.addEventListener(FORM_SUBMITTED_EVENT, onFormSubmitted);
    return () => window.removeEventListener(FORM_SUBMITTED_EVENT, onFormSubmitted);
  }, []);

  // Extract tabs for the right column
  const [rightColumnTabs, setRightColumnTabs] = useState<TwoColumnPropertiesType['tabs']>(
    tabs.filter((tab) => tab.column === 'right'),
  );
  useEffect(() => setRightColumnTabs(tabs.filter((tab) => tab.column === 'right')), [tabs]);

  // Fetch hero background image
  const contentUrl = useWindowLocation();
  const { getResponsiveImage } = useCloudinary();

  const heroImageUrl = (devicePR: number) => {
    const image = getResponsiveImage(heroImage?.publicId, {
      width: 1920,
      height: 1080,
      quality: 'auto:good',
      devicePR,
    });
    if (image && darkenBackground) {
      image.adjust(Adjust.opacity(60)).backgroundColor('black');
    }
    return image?.toURL();
  };
  const backgroundX1 = heroImageUrl(1);
  const backgroundX2 = heroImageUrl(2);
  const background = backgroundX1 && backgroundX2 ? { x1: backgroundX1, x2: backgroundX2 } : undefined;

  // Memoise hero to prevent page bouncing on rerender
  const Hero = useMemo(() => <HeroContent heroContent={heroContent} />, [heroContent]);

  return (
    <Layout
      title={data.seoConfig?.title || data.name}
      description={data.seoConfig?.introduction || data.ingress || ''}
      gtmArgs={{ page_type: 'TwoColumnApp', pageCategory: data.slug, contentId: data.name }}
      imgUrl={linkPreview?.srcSet?.jpgLarge || undefined}
      internalSearchMetaData={{ cardType: InternalSearchCardType.CategoryApp }}
    >
      <S.PageContent $background={background} $halfHeightBackground={isValgPage}>
        {isValgPage ? (
          <S.ChatBubbleWrapper>
            <S.ChatBubble>
              <S.ChatBubbleTitle>
                {isFormSubmitted ? 'Takk for at du sier i fra!' : 'Vi vil høre fra deg!'}
              </S.ChatBubbleTitle>
            </S.ChatBubble>
          </S.ChatBubbleWrapper>
        ) : (
          Hero
        )}
        <S.ColumnContainer>
          <S.LeftColumn $isValgPage={isValgPage} $isFormSubmitted={isFormSubmitted}>
            <ErrorBoundary>
              <TabContainer
                tabs={tabs}
                isLeftColumn
                slug={data.slug}
                tabTitle={searchParams.get('leftColumn')}
                onTabChange={(tabTitle) => updateSearchParams('leftColumn', tabTitle)}
              />
            </ErrorBoundary>
          </S.LeftColumn>
          {!isValgPage && (
            <S.RightColumn>
              <ErrorBoundary>
                <TabContainer
                  tabs={rightColumnTabs}
                  isLeftColumn={false}
                  slug={data.slug}
                  tabTitle={searchParams.get('rightColumn')}
                  onTabChange={(tabTitle) => updateSearchParams('rightColumn', tabTitle)}
                />
              </ErrorBoundary>
            </S.RightColumn>
          )}
        </S.ColumnContainer>
        {data.showFeedbackSurvey && (
          <S.FeedbackContainer>
            <FeedbackSurvey contentUrl={contentUrl} />
          </S.FeedbackContainer>
        )}
      </S.PageContent>
    </Layout>
  );
};
